import { Trash2, Car, Pencil, Smartphone, RectangleHorizontal, CalendarClock, BadgeCheck, CreditCard, RefreshCw, Gift, Clock } from 'lucide-react';
import PlateVisual from './plates/PlateVisual';
import PhoneNumberLockup from './PhoneNumberLockup';
import OperatorLogo from './OperatorLogo';
import { useOperatorView } from '../hooks/usePhoneOperators';
import { monthLabel } from './AdPlanPicker';


const PhoneAdOperator = ({ ad, isAr, muted }) => {
  const op = useOperatorView(ad);
  const name = isAr ? op.nameAr : op.nameEn;
  if (!name) return <span className={`text-xs ${muted}`}>{isAr ? 'رقم هاتف' : 'Phone Number'}</span>;
  return (
    <>
      <OperatorLogo src={op.logo} name={name} accent={op.accent} className="w-7 h-7" letterClass="text-xs" testId={`myad-phone-operator-${ad.id}`} />
      <span className={`text-xs ${muted}`}>{name}</span>
    </>
  );
};

const CATEGORY_LABELS = {
  vip: { ar: 'مميز جداً', en: 'VIP', cls: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  golden: { ar: 'ذهبي', en: 'Golden', cls: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  silver: { ar: 'فضي', en: 'Silver', cls: 'bg-slate-400/15 text-slate-400 border-slate-400/30' },
  normal: { ar: 'عادي', en: 'Normal', cls: 'bg-[#1E9BD9]/10 text-[#1E9BD9] border-[#1E9BD9]/30' },
};

export const STATUS_LABELS = {
  pending: { ar: 'قيد المراجعة', en: 'Pending', cls: 'bg-amber-500/15 text-amber-500' },
  pending_payment: { ar: 'بانتظار الدفع', en: 'Awaiting payment', cls: 'bg-orange-500/15 text-orange-500' },
  active: { ar: 'نشط', en: 'Active', cls: 'bg-emerald-500/15 text-emerald-500' },
  expired: { ar: 'منتهي', en: 'Expired', cls: 'bg-red-500/15 text-red-500' },
  stopped: { ar: 'موقوف', en: 'Stopped', cls: 'bg-red-500/15 text-red-500' },
  rejected: { ar: 'مرفوض', en: 'Rejected', cls: 'bg-red-500/15 text-red-500' },
};

const TYPE_META = {
  phones: { icon: Smartphone, ar: 'رقم هاتف', en: 'Phone number', color: 'text-emerald-500' },
  plates: { icon: RectangleHorizontal, ar: 'لوحة سيارة', en: 'Car plate', color: 'text-[#F59E0B]' },
  vehicles: { icon: Car, ar: 'مركبة', en: 'Vehicle', color: 'text-[#1E9BD9]' },
};

const fmtDate = (iso, isAr) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(isAr ? 'ar-AE' : 'en-AE', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const daysLeft = (iso) => {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
};

/**
 * MyAdCard — unified "ad frame" for phones / plates / vehicles:
 *   [header strip: type + status]  [body preview]  [footer: price + plan/expiry]  [actions]
 */
export const MyAdCard = ({ ad, category, isAr, isDarkMode, onDelete, onEdit, onPay, onCheckPayment, checkingPayment }) => {
  const details = ad.details || {};
  const meta = TYPE_META[category] || TYPE_META.vehicles;
  const TypeIcon = meta.icon;
  const status = STATUS_LABELS[ad.status] || STATUS_LABELS.active;
  const isFree = ad.pricing ? ad.pricing.is_free !== false : true;
  const left = daysLeft(ad.expires_at);

  const frame = isDarkMode
    ? 'bg-[#121520] border border-[#1E9BD9]/20 hover:border-[#1E9BD9]/50 shadow-[0_8px_30px_rgba(0,0,0,0.35)]'
    : 'bg-white border border-[#1E9BD9]/25 hover:border-[#1E9BD9]/60 shadow-sm hover:shadow-lg';
  const strip = isDarkMode ? 'bg-white/[0.04] border-white/10' : 'bg-slate-50 border-slate-200/80';
  const muted = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const text = isDarkMode ? 'text-white' : 'text-[#0F172A]';
  const actionBtn = `inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold transition-colors ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-slate-200' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`;

  const renderBody = () => {
    if (category === 'phones') {
      const cat = CATEGORY_LABELS[details.number_category];
      return (
        <div className="flex flex-col items-center justify-center gap-2.5 h-full">
          <div className="flex items-center gap-2">
            <PhoneAdOperator ad={ad} isAr={isAr} muted={muted} />
            {cat && (
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cat.cls}`} data-testid={`myad-phone-cat-${ad.id}`}>
                {isAr ? cat.ar : cat.en}
              </span>
            )}
          </div>
          <PhoneNumberLockup value={details.full_number || ad.title} size="md" testId={`myad-phone-number-${ad.id}`} />
        </div>
      );
    }
    if (category === 'plates') {
      const hasPlateData = ad.plateNumber || details.plate?.number;
      return hasPlateData ? (
        <PlateVisual
          emirate={ad.emirate || details.plate?.emirate}
          category={ad.plateType || details.plate?.category}
          code={ad.plateCode || details.plate?.code}
          number={ad.plateNumber || details.plate?.number}
          width={200}
          showCodeOnPlate={ad.show_on_plate ?? details.plate?.show_on_plate}
        />
      ) : (
        <p className={`text-lg font-bold ${text}`}>{ad.title}</p>
      );
    }
    const img = ad.images?.[0] || ad.image;
    return img ? (
      <img src={img} alt={ad.title} className="absolute inset-0 w-full h-full object-cover" />
    ) : (
      <Car className="h-10 w-10 text-[#94A3B8]" />
    );
  };

  return (
    <div className={`relative flex flex-col h-full rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-0.5 ${frame}`} data-testid={`myad-${category === 'vehicles' ? 'vehicle' : category === 'plates' ? 'plate' : 'phone'}-${ad.id}`}>
      <div className="h-[2px] w-full bg-gradient-to-r from-[#1E9BD9] via-[#38BDF8] to-[#F5A962] opacity-80" />

      {/* Header strip */}
      <div className={`px-3.5 py-2 border-b flex items-center justify-between text-xs ${strip}`}>
        <span className={`inline-flex items-center gap-1.5 font-semibold ${muted}`}>
          <TypeIcon className={`h-3.5 w-3.5 ${meta.color}`} />
          {isAr ? meta.ar : meta.en}
        </span>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${status.cls}`} data-testid={`myad-status-${ad.id}`}>
          {isAr ? status.ar : status.en}
        </span>
      </div>

      {/* Body preview */}
      <div className={`relative h-36 flex items-center justify-center p-4 ${category === 'vehicles' ? '' : isDarkMode ? 'bg-white/[0.02]' : 'bg-slate-50/60'}`}>
        {renderBody()}
        {category === 'vehicles' && (
          <div className="absolute inset-x-0 bottom-0 px-3 py-2 bg-gradient-to-t from-black/70 to-transparent">
            <h4 className="font-bold text-sm text-white truncate">{ad.title}</h4>
          </div>
        )}
      </div>

      {/* Footer: price + plan */}
      <div className={`mt-auto px-3.5 py-2.5 border-t flex items-center justify-between gap-2 ${isDarkMode ? 'border-white/10' : 'border-slate-200/80'}`}>
        <p className="font-black text-[#F59E0B] tabular-nums" dir="ltr">{Number(ad.price || 0).toLocaleString()} <span className="text-[11px] font-semibold">AED</span></p>
        <span className={`inline-flex items-center gap-1 text-[11px] ${muted}`} data-testid={`myad-plan-${ad.id}`}>
          {isFree ? <Gift className="h-3 w-3 text-emerald-500" /> : <BadgeCheck className="h-3 w-3 text-[#1E9BD9]" />}
          {isFree ? (isAr ? 'مجاني' : 'Free') : `${isAr ? 'مدفوع' : 'Paid'}${ad.pricing?.months ? ` · ${monthLabel(ad.pricing.months, isAr)}` : ''} · ${ad.pricing?.fee} ${ad.pricing?.currency || 'AED'}`}
        </span>
      </div>
      {ad.expires_at && (
        <div className={`px-3.5 pb-2 -mt-1 inline-flex items-center gap-1 text-[11px] ${left !== null && left <= 5 ? 'text-red-500' : muted}`} data-testid={`myad-expiry-${ad.id}`}>
          <CalendarClock className="h-3 w-3" />
          {ad.status === 'expired'
            ? (isAr ? `انتهى في ${fmtDate(ad.expires_at, isAr)}` : `Expired ${fmtDate(ad.expires_at, isAr)}`)
            : (isAr ? `ينتهي ${fmtDate(ad.expires_at, isAr)} (${left} يوم)` : `Expires ${fmtDate(ad.expires_at, isAr)} (${left}d)`)}
        </div>
      )}
      {ad.status === 'pending_payment' && ad.payment && (
        <div className={`px-3.5 pb-2 -mt-1 inline-flex items-center gap-1 text-[11px] ${muted}`}>
          <Clock className="h-3 w-3" />
          {isAr ? `مرجع الدفع: ${ad.payment.reference}` : `Payment ref: ${ad.payment.reference}`}
        </div>
      )}

      {/* Actions */}
      <div className={`px-3.5 py-2.5 border-t flex items-center gap-2 flex-wrap ${isDarkMode ? 'border-white/10 bg-white/[0.02]' : 'border-slate-200/80 bg-slate-50/60'}`}>
        {ad.status === 'pending_payment' ? (
          <>
            <button type="button" onClick={() => onPay?.(category, ad)} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:shadow-[0_4px_14px_rgba(30,155,217,0.45)] transition-shadow" data-testid={`pay-ad-${ad.id}`}>
              <CreditCard className="h-3.5 w-3.5" />
              {isAr ? 'ادفع الآن' : 'Pay now'}
            </button>
            <button type="button" onClick={() => onCheckPayment?.(category, ad)} disabled={checkingPayment} className={actionBtn} data-testid={`check-payment-${ad.id}`}>
              <RefreshCw className={`h-3.5 w-3.5 ${checkingPayment ? 'animate-spin' : ''}`} />
              {isAr ? 'تحقق من الدفع' : 'Check payment'}
            </button>
          </>
        ) : (
          <button type="button" onClick={() => onEdit?.(category, ad)} className={actionBtn} data-testid={`edit-ad-${ad.id}`}>
            <Pencil className="h-3.5 w-3.5" />
            {isAr ? 'تعديل' : 'Edit'}
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(category, ad.id)}
          className={`ms-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-red-500 transition-colors ${isDarkMode ? 'hover:bg-red-500/10' : 'hover:bg-red-50'}`}
          data-testid={`delete-ad-${ad.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
          {isAr ? 'حذف' : 'Delete'}
        </button>
      </div>
    </div>
  );
};
